import { NetworkIcon } from '@application/components';
import { formatUtcDateOnly } from '@application/utils/date-utils';
import { Site } from '@domain/graphql.types';
import { ArrowBack, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Button, CircularProgress, Divider, IconButton, Typography } from '@mui/material';
import { forwardRef, useContext, useId } from 'react';
import { useTranslation } from 'react-i18next';
import DataSourceControl from '../components/DataSourceControl';
import { ToHistogramBuckets } from '../components/HistoricalViewContainer';
import { humanizeApiValue, humanizeApiValues } from '../components/humanize';
import MapPin, { getMapPinVariant, mapPinVariantLabelKey } from '../components/MapPin';
import { WeeklyStationCharts } from '../components/StationCharts';
import SiteLookupContext from '../context/SiteLookupContext';
import { siteLookupTokens } from '../siteLookupTokens';
import RealTimeStationDetail from './RealTimeStationDetail';
import { getNetworkName, getPortsLabel } from './stationSummary';

const { color, size } = siteLookupTokens;

type Props = {
  site: Site;
  /** position (0-based) of the station in the list, and the number of stations */
  index: number;
  count: number;
  onBack: () => void;
  onPrevious: () => void;
  onNext: () => void;
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <Box minWidth={0}>
    <Typography fontSize={12} color={color.text.muted}>
      {label}
    </Typography>
    <Typography fontSize={14} color={color.text.primary} sx={{ overflowWrap: 'anywhere' }}>
      {value}
    </Typography>
  </Box>
);

const HistoricalStationDetail = ({ site }: { site: Site }) => {
  const { t, i18n } = useTranslation();
  const hv = site.historicalView;

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <Box display="grid" gridTemplateColumns="1fr 1fr" columnGap={2} rowGap={1.5}>
        <Field label={t('siteLookup.detail.historical.opened')} value={site.openDate ? formatUtcDateOnly(site.openDate, i18n.language) : '—'} />
        <Field label={t('siteLookup.detail.historical.connectors')} value={humanizeApiValues(hv?.portTypes)} />
        <Field label={t('siteLookup.detail.historical.hardware')} value={humanizeApiValues(hv?.hardwareTypes)} />
        <Field label={t('siteLookup.detail.historical.pricing')} value={humanizeApiValue(hv?.pricingModel)} />
      </Box>
      <WeeklyStationCharts buckets={ToHistogramBuckets(hv?.utilizationSeries?.buckets)} />
    </Box>
  );
};

/** The sheet of one station, in place of the list. It takes the focus when it opens. */
const StationDetail = forwardRef<HTMLDivElement, Props>(({ site, index, count, onBack, onPrevious, onNext }, ref) => {
  const { t } = useTranslation();
  const { dataView, hasHistoricalAccess, hasRealTimeAccess, pendingRequest, result } = useContext(SiteLookupContext);
  const titleId = useId();

  const isReportCapped = !!result?.data.limitExceeded;
  // The status pin of the station, as on the map before it is selected: the selected pin would hide the status.
  const pinVariant = getMapPinVariant(site, { isReportCapped });
  const statusLabel = t(mapPinVariantLabelKey[pinVariant]);
  const stationsCount = result?.data.stationsCount ?? count;
  const facts = [
    getNetworkName(site, t),
    t('siteLookup.list.distance', { count: +site.distance.toFixed(2) }),
    getPortsLabel(site, t),
    t('siteLookup.list.power', { power: site.maxSpeed }),
  ];

  return (
    <Box ref={ref} component="section" tabIndex={-1} aria-labelledby={titleId} sx={{ outline: 'none', paddingX: 1.5, paddingBottom: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} marginBottom={1.5}>
        <Button
          onClick={onBack}
          startIcon={<ArrowBack fontSize="small" />}
          aria-label={t('siteLookup.detail.backLabel', { count: stationsCount })}
          sx={{ color: color.link, fontWeight: 600, textTransform: 'none', marginLeft: -1 }}
        >
          {t('siteLookup.list.stations', { count: stationsCount })}
        </Button>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography fontSize={13} color={color.text.secondary} whiteSpace="nowrap">
            {t('siteLookup.detail.position', { index: index + 1, count })}
          </Typography>
          <IconButton size="small" onClick={onPrevious} disabled={count < 2} aria-label={t('siteLookup.detail.previous')}>
            <ChevronLeft fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onNext} disabled={count < 2} aria-label={t('siteLookup.detail.next')}>
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box display="flex" alignItems="flex-start" gap={1.5}>
        <NetworkIcon
          networkCode={site.networkCode}
          iconUrl={site.networkIconUrl ?? undefined}
          sx={{ borderRadius: 0.5, flexShrink: 0, height: size.sheetLogo, width: size.sheetLogo }}
        />
        <Box flex={1} minWidth={0}>
          <Typography
            id={titleId}
            component="h2"
            fontSize={18}
            fontWeight={600}
            lineHeight={1.3}
            color={color.text.primary}
            sx={{ overflowWrap: 'anywhere', textTransform: 'capitalize' }}
          >
            {site.name.toLowerCase()}
          </Typography>
          <Typography fontSize={13} color={color.text.secondary} marginTop={0.5}>
            {facts.join(' · ')}
          </Typography>
        </Box>
        <Box role="img" aria-label={statusLabel} lineHeight={0} flexShrink={0}>
          <MapPin height={28} includedInReport={site.includedInReport} variant={pinVariant} width={28} />
        </Box>
      </Box>

      {(hasHistoricalAccess || hasRealTimeAccess) && (
        <Box marginTop={2}>
          <DataSourceControl />
        </Box>
      )}
      <Divider sx={{ marginY: 2, borderColor: color.border }} />

      {pendingRequest ? (
        <Box display="flex" justifyContent="center" paddingY={4}>
          <CircularProgress size={28} aria-label={t('siteLookup.detail.loading')} />
        </Box>
      ) : dataView === 'realtime' && hasRealTimeAccess ? (
        <RealTimeStationDetail key={site.id} site={site} />
      ) : hasHistoricalAccess ? (
        <HistoricalStationDetail site={site} />
      ) : null}
    </Box>
  );
});

export default StationDetail;
